import { httpsCallable } from '@firebase/functions';
import { collection, limit, query, where, type Unsubscribe } from '@firebase/firestore';
import { functions, db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { StructureSchema } from '../schemas/firestoreSchemas';
import { mapDoc } from '@utils/firestore';
import type { Structure } from '../types';

export interface AddUserStructureInput {
  address: string;
  city: string;
  state: string;
  zip?: string;
  propertyType?: string;
  centroid: { latitude: number; longitude: number };
  // Defaults to true server-side. Post-attach passes false so attaching an
  // observation to a new address doesn't claim the property.
  claim?: boolean;
}

export interface AddUserStructureResult {
  structureId: string;
  serviceAreaId: string;
  serviceAreaName: string;
  /** The function found a structure at this address already and reused it. */
  existing?: boolean;
}

const callable = httpsCallable<AddUserStructureInput, AddUserStructureResult>(
  functions,
  'addUserStructure'
);

export async function callAddUserStructure(
  input: AddUserStructureInput
): Promise<AddUserStructureResult> {
  const res = await callable(input);
  return res.data;
}

/**
 * Live-subscribe to every user-added structure so clients can render the
 * newly created ones alongside the static GeoJSON shards. We deliberately
 * don't filter by serviceAreaId here — service areas aren't a user-facing
 * concept right now, and user-added volume is small enough that a global
 * subscription is fine. When areas come back as a feature we can re-scope
 * (where('serviceAreaId', 'in', activeAreaIds), like the claims pattern).
 * The limit is a safety cap so this can't silently become a full-collection
 * stream as volume grows; plain limit avoids a composite-index requirement.
 */
const USER_ADDED_LIMIT = 500;

export function subscribeToUserAddedStructures(
  onChange: (structures: Structure[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, COLLECTIONS.STRUCTURES),
    where('source', '==', 'user-added'),
    limit(USER_ADDED_LIMIT)
  );
  return onSnapshotWithRetry(
    q,
    (snap) => {
      const results: Structure[] = [];
      for (const d of snap.docs) {
        const parsed = mapDoc(d, StructureSchema);
        if (parsed) results.push(parsed);
      }
      onChange(results);
    },
    (err) => onError?.(err)
  );
}
